import { useMemo, useState } from "react";
import { CalendarDays, CalendarRange, Calendar, Layers, Check } from "lucide-react";

const fmt = (n) => `${Number(n || 0).toLocaleString()} RWF`;
const PERIODS = [["daily", "Daily", CalendarDays], ["weekly", "Weekly", CalendarRange], ["monthly", "Monthly", Calendar], ["other", "Other", Layers]];
const period = (d = "") => {
  const s = d.toLowerCase();
  if (/day|daily|24\s*h|hour/.test(s)) return "daily";
  if (/week|7\s*d/.test(s)) return "weekly";
  if (/month|30\s*d/.test(s)) return "monthly";
  return "other";
};
const slug = (p) => p.toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Voice & data bundles: Daily / Weekly / Monthly first → bundle type → bundle.
export const BundlePicker = ({ bundles, value, onPick }) => {
  const all = useMemo(() => bundles.map((b) => ({ ...b, period: period(b.duration) })), [bundles]);
  const periods = PERIODS.filter(([k]) => all.some((b) => b.period === k));
  const [per, setPer] = useState(null);
  const p = periods.some(([k]) => k === per) ? per : periods[0]?.[0];
  const pkgs = [...new Set(all.filter((b) => b.period === p).map((b) => b.package))];
  const [pkg, setPkg] = useState(null);
  const k = pkgs.includes(pkg) ? pkg : pkgs[0];
  const items = all.filter((b) => b.period === p && b.package === k).sort((a, b) => a.price - b.price);
  return (
    <div className="min-w-0" data-testid="util-bundle-picker">
      <div className="text-sm font-bold text-white mb-2">1. How long should it last?</div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" role="tablist">
        {periods.map(([key, label, I]) => (
          <button key={key} role="tab" aria-selected={p === key} onClick={() => setPer(key)} data-testid={`util-bundle-dur-${key}`}
            className={`h-16 rounded-xl border-2 flex flex-col items-center justify-center gap-1 font-extrabold text-base transition-colors ${p === key ? "bg-white text-black border-white" : "bg-black text-white border-white/30 hover:border-white"}`}>
            <I className="w-5 h-5" />{label}
          </button>))}
      </div>
      {pkgs.length > 1 && (<>
        <div className="text-sm font-bold text-white mt-5 mb-2">2. Choose the type</div>
        <div className="flex gap-2 overflow-x-auto pb-1">{pkgs.map((x) => (
          <button key={x} onClick={() => setPkg(x)} data-testid={`util-bundle-pkg-${slug(x)}`}
            className={`h-11 px-4 rounded-full text-sm font-bold border-2 whitespace-nowrap transition-colors ${x === k ? "bg-[#E11D2A] border-[#E11D2A] text-white" : "bg-black border-white/30 text-white hover:border-white"}`}>{x}</button>))}</div>
      </>)}
      <div className="text-sm font-bold text-white mt-5 mb-2">{pkgs.length > 1 ? "3." : "2."} Pick your bundle</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-96 overflow-y-auto pr-1" data-testid="util-bundle-list">
        {items.map((b) => { const on = value === b.id; return (
          <button key={b.id} onClick={() => onPick(b)} data-testid={`util-bundle-${b.id}`} aria-pressed={on}
            className={`relative text-left rounded-xl border-2 p-4 bg-black transition-colors ${on ? "border-[#E11D2A]" : "border-white/25 hover:border-white"}`}>
            {on && <Check className="absolute right-3 top-3 w-5 h-5 text-[#E11D2A]" />}
            <div className="text-base text-white font-bold pr-6">{b.label}</div>
            <div className="text-lg text-white font-extrabold mt-1">{fmt(b.price)}</div>
            {b.description && <div className="text-sm text-slate-200 mt-1">{b.description}</div>}
          </button>); })}
        {!items.length && <div className="text-sm text-white">No bundles in this period.</div>}
      </div>
    </div>
  );
};
